import { extractForTTS, extractTextFromHtml } from "@educationgalaxy/tts-core";
import type { Language } from "@educationgalaxy/tts-core";
import { Buffer } from "buffer";
import { getSpeechFromText } from "../../api/textToSpeech.service";
import constant from "../constant";
import { waitForElementsToRenderWithMutation } from "../waitFor";
import { speakerClass } from "./textToSpeechConfig";

let selectedAudio;
export const bindTTS = (item: any, language: Language) => {
  language = language == "es" ? "es" : "en";

  const rootClass = "custom-lrn-preview";

  waitForElementsToRenderWithMutation(
    ".main-content.custom-lrn-preview",
    `.${rootClass} .lrn-mcq-option, .${rootClass} .lrn_question, .${rootClass} .lrn_sharedpassage, .${rootClass} .lrn_btn_drag.lrn_draggable, .${rootClass} .lrn_response_input`,
    1,
    3000
  ).then((lrnElements) => {
    if (!lrnElements || lrnElements.length < 1) return;
    removeSpeechButtons();

    lrnElements.forEach((element: Element, index) => {
      // Add the lrn-mcq-option-with-tts class to elements with lrn-mcq-option class
      if (element.classList.contains("lrn-mcq-option")) {
        element.classList.add("lrn-mcq-option-with-tts");
      }

      const elementClassName = getElementClass(element);

      // Skip prompts whose only content is draggables/dropzones (image cloze).
      if (
        elementClassName === speakerClass.dragDropPrompt.app &&
        !hasSpeakableDragDropPromptText(element as HTMLElement)
      ) {
        return;
      }

      const questionReference = getQuestionReference(element);
      const entityReference = getElementEntityReference(element);
      const speechButton = getAudioButtonElement(
        elementClassName,
        entityReference ?? questionReference,
        index
      );
      const audioElement = getAudioElement(
        elementClassName,
        entityReference ?? questionReference,
        index
      );

      const host = getButtonHostElement(element);
      host.insertAdjacentElement("afterbegin", speechButton);
      host.insertAdjacentElement("afterbegin", audioElement);

      const button = document.querySelector(
        "#btn" + elementClassName + index
      ) as HTMLButtonElement;

      const audio = document.querySelector(
        "#audio" + elementClassName + index
      ) as HTMLAudioElement;

      button.addEventListener("click", async (event) => {
        try {
          await handleClickSpeechButton(
            event,
            item,
            language,
            audio,
            questionReference,
            entityReference,
            element as HTMLElement
          );
        } catch (_error) {
          resetAudio();
        }
      });
    });
  });
};

const hasSpeakableDragDropPromptText = (element: HTMLElement): boolean => {
  const clone = element.cloneNode(true) as HTMLElement;
  clone
    .querySelectorAll(
      ".lrn_btn_drag, .lrn_draggable, .lrn_dropzone, .lrn_cloze_response, " +
        ".lrn_possibilityList, .lrn_icon, .sr-only, button, img"
    )
    .forEach((el) => el.remove());
  return !!clone.textContent?.trim();
};

// Table prompts (type 34) inject into the parent so the <div> child isn't invalid inside <table>.
const getButtonHostElement = (element: Element): Element => {
  if (
    element.classList.contains(speakerClass.dragDropPrompt.learnosity) &&
    element.tagName.toUpperCase() === "TABLE"
  ) {
    return element.parentElement ?? element;
  }
  return element;
};

export const getTextFromLearnosityPassageContent = async (
  text: string,
  lang: Language
) => {
  const safeLang = lang === "es" ? "es" : "en";
  return extractTextFromHtml(text || "", safeLang);
};

const getElementClass = (element: Element) => {
  if (element.classList.contains(speakerClass.option.learnosity))
    return speakerClass.option.app;
  else if (element.classList.contains(speakerClass.feature.learnosity))
    return speakerClass.feature.app;
  else if (
    element.classList.contains(speakerClass.dragDropOption.learnosity) &&
    element.classList.contains("lrn_draggable")
  )
    return speakerClass.dragDropOption.app;
  else if (element.classList.contains(speakerClass.dragDropPrompt.learnosity))
    return speakerClass.dragDropPrompt.app;
  return speakerClass.question.app;
};

const getElementEntityReference = (element: Element) => {
  if (element?.classList?.contains(speakerClass.option.learnosity)) {
    return element?.getElementsByTagName("input")[0]?.value;
  } else if (element?.classList?.contains(speakerClass.feature.learnosity)) {
    return (
      element
        ?.closest("span[data-authorapi-widget-reference]")
        ?.getAttribute("data-authorapi-widget-reference") ?? null
    );
  } else if (
    element?.classList?.contains(speakerClass.dragDropOption.learnosity) ||
    element?.classList?.contains(speakerClass.dragDropPrompt.learnosity)
  ) {
    return (
      element.getAttribute("data-key") ??
      element.getAttribute("data-response-index") ??
      element.getAttribute("data-index") ??
      null
    );
  }
  return null;
};

const getQuestionReference = (element: any) => {
  return element
    ?.closest("span[data-authorapi-widget-reference]")
    ?.getAttribute("data-authorapi-widget-reference");
};

const removeSpeechButtons = () => {
  const speechDivs = document.querySelectorAll(
    ".div_speaker_question, .div_speaker_feature, .div_speaker_options, .div_speaker_dragdrop_options, .div_speaker_dragdrop_prompt"
  ) as NodeListOf<Element>;
  speechDivs?.forEach((div) => {
    div.remove();
  });
};

const getAudioButtonElement = (
  elementClassName: string,
  reference: string,
  buttonIndex: number
) => {
  const divElement = document.createElement("div");
  divElement.className = elementClassName;
  divElement.id = reference + "_button_" + buttonIndex;
  divElement.innerHTML =
    "<button type='button' id='btn" +
    elementClassName +
    buttonIndex +
    "'" +
    " aria-label='Text To Speech' class='lrn_btn custom_btn text-to-speech " +
    elementClassName +
    " test-speaker-off pos-middle text-to-speech-custom' " +
    "data-original-title='Text To Speech' " +
    "style='z-index:9999'>" +
    "</button>";
  return divElement;
};

const getAudioElement = (
  elementClassName: string,
  questionId: string,
  audioIndex: number
) => {
  const audioElement = document.createElement("div");
  audioElement.className = elementClassName;
  audioElement.id = questionId + "_audio_" + audioIndex;
  audioElement.innerHTML =
    "<audio type='audio' id='audio" +
    elementClassName +
    audioIndex +
    "'" +
    " aria-label='Text To Speech' class='custom_audio_tts" +
    elementClassName +
    " pos-middle' style='z-index:9999'  src=''></audio>";
  return audioElement;
};

const updateSpeakerIcon = (element: HTMLElement | null = null) => {
  const btnSpeakers = document.querySelectorAll(
    ".text-to-speech-custom"
  ) as NodeListOf<Element>;
  if (element) {
    btnSpeakers?.forEach((btn) => {
      if (btn.id !== element.id) {
        btn.classList.add("text-to-speech-disable");
        btn.classList.add("pointer-events-none");
      }
    });
    if (element.classList.contains("test-speaker-on")) {
      element.classList.remove("test-speaker-on");
      element.classList.add("test-speaker-off");
    } else {
      element.classList.remove("test-speaker-off");
      element.classList.add("test-speaker-on");
    }
  } else {
    btnSpeakers?.forEach((btn) => {
      btn.classList.remove("text-to-speech-disable");
      btn.classList.remove("pointer-events-none");
      if (btn.classList.contains("test-speaker-on"))
        btn.classList.remove("test-speaker-on");
      if (!btn.classList.contains("test-speaker-off"))
        btn.classList.add("test-speaker-off");
    });
  }
};

const hold = () => {
  if (selectedAudio != null) {
    selectedAudio.pause();
    selectedAudio.currentTime = 0;
    selectedAudio = null;
  }
};

const resetAudio = () => {
  updateSpeakerIcon();
  hold();
};

const getPageTextContent = (element: HTMLElement, pages: string[]) => {
  if (!element || !pages) return "";
  let index = -1;
  const span = element
    .closest("div .lrn_sharedpassage")
    ?.querySelector(".lrn-sharedpassage-index");
  if (span) {
    const number = span.textContent?.trim()?.split(" of ")[0];
    const order = number ? parseInt(number) : -1;
    index = order - 1;
  }
  return pages[index] ?? "";
};

const getTTSElementText = async (
  element: HTMLElement,
  questionReference: string | null,
  entityReference: string | null,
  language: Language,
  item: any,
  sourceElement?: HTMLElement | null
) => {
  const safeLang = language === "es" ? "es" : "en";

  // Drag-drop reads from DOM since per-token text isn't in the item JSON.
  if (
    sourceElement &&
    element.classList.contains(speakerClass.dragDropOption.app)
  ) {
    const tokenText =
      (sourceElement.querySelector(".lrn_item") as HTMLElement | null) ??
      sourceElement;
    const extraction = await extractForTTS(tokenText, {
      language: safeLang,
      handleAriaLabels: false,
      skipSelectors: [".sr-only", ".lrn_icon"],
    });
    return extraction.fullText;
  }

  if (
    sourceElement &&
    element.classList.contains(speakerClass.dragDropPrompt.app)
  ) {
    const isClassificationTable =
      sourceElement.tagName.toUpperCase() === "TABLE" &&
      sourceElement.classList.contains("lrn_classification_table");
    const extraction = await extractForTTS(sourceElement, {
      language: safeLang,
      handleAriaLabels: false,
      skipTables: isClassificationTable ? false : undefined,
      skipSelectors: [
        ".lrn_btn_drag",
        ".lrn_draggable",
        ".lrn_dropzone",
        ".lrn_cloze_response",
        ".lrn_possibilityList",
        ".lrn_icon",
        ".sr-only",
      ],
      insertBlankForSelectors: [],
      blankWord: "",
    });
    return extraction.fullText;
  }

  const divParentElement = document.createElement("div");
  const question = item?.questions?.find(
    (question) => question?.reference === questionReference
  );

  if (element.classList.contains(speakerClass.option.app)) {
    const selectedOption = question?.data?.options?.find(
      (option) => option.value === entityReference
    );
    const optionText = selectedOption?.assistive_label?.label
      ? selectedOption.assistive_label?.label
      : selectedOption?.label;

    divParentElement.innerHTML = optionText || "";
  } else if (element.classList.contains(speakerClass.question.app)) {
    const questionText = question?.data?.stimulus || "";
    divParentElement.innerHTML = questionText.trim();
  } else if (element.classList.contains(speakerClass.feature.app)) {
    const feature = item?.features?.find(
      (feature) => feature?.reference === entityReference
    )?.data;
    const featureText = feature?.paginated_content
      ? getPageTextContent(element, feature?.pages)
      : feature?.content;
    divParentElement.innerHTML = featureText?.trim() || "";
  }

  const extraction = await extractForTTS(divParentElement, {
    language: safeLang,
  });
  return extraction.fullText;
};

const callSpeechFromPolly = async (text: string, speakLanguage: Language) => {
  text = text.substring(0, constant.TTS.MAX_LENGTH_STORAGE_TTS);
  return await getSpeechFromText(text, speakLanguage)
    .then((resp) => {
      if (!resp.data.byteLength) return;
      const buffer = Buffer.from(resp.data);
      return new Blob([buffer], { type: "audio/mp3" });
    })
    .catch(() => {
      return;
    });
};

const playItemAudio = async (
  audio: HTMLAudioElement,
  text: string,
  language: Language
) => {
  const speakLanguage = language === "es" ? "es" : "en";
  if (text == null || text.length === 0) {
    resetAudio();
    return;
  }
  selectedAudio = audio;
  const blob = await callSpeechFromPolly(text, speakLanguage);
  if (blob == null) {
    resetAudio();
    return;
  }
  const url = window.URL.createObjectURL(blob);
  audio.src = url;
  audio.pause();
  audio.load();
  if (selectedAudio == null) return;
  audio.play();
  audio.onended = () => {
    resetAudio();
  };
};

const handleClickSpeechButton = async (
  event: MouseEvent,
  item: any,
  language: Language,
  audioElement: HTMLAudioElement,
  questionReference: string | null,
  entityReference: string | null,
  sourceElement?: HTMLElement | null
) => {
  if (!event?.target) return;
  const button = event.target as HTMLElement;
  if (button.classList.contains("test-speaker-on")) {
    resetAudio();
    return;
  }
  updateSpeakerIcon(button);
  const text =
    (await getTTSElementText(
      button,
      questionReference,
      entityReference,
      language,
      item,
      sourceElement
    )) || "";
  await playItemAudio(audioElement, text, language);
};
